/**
 * Shared Calendar Utilities
 * Common functions used by both CalendarWidget and CalendarList
 */

/**
 * Placeholder text for the date filter button (from Neos data-filter-label-date).
 * @param {HTMLElement} container - Root calendar-list or calendar-widget element
 * @returns {string}
 */
export function getFilterLabelDatePlaceholder(container) {
    return container.dataset.filterLabelDate || 'Vælg dato';
}

// Constants
const MONTH_NAMES_CAPITALIZED = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 
                                  'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
const MONTH_NAMES_LOWERCASE = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 
                               'juli', 'august', 'september', 'oktober', 'november', 'december'];
const MONTH_NAMES_ABBREVIATED = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

/**
 * Format date as ISO string (YYYY-MM-DD) without timezone conversion
 * @param {number} year - Year
 * @param {number} month - Month (0-indexed)
 * @param {number} day - Day of month
 * @returns {string} ISO date string
 */
function formatDateISO(year, month, day) {
    const paddedMonth = String(month + 1).padStart(2, '0');
    const paddedDay = String(day).padStart(2, '0');
    return `${year}-${paddedMonth}-${paddedDay}`;
}

/**
 * Format date as Danish text (e.g., "27. marts 2026")
 * @param {number} year - Year
 * @param {number} month - Month (0-indexed)
 * @param {number} day - Day of month
 * @returns {string} Formatted date string
 */
function formatDateDanish(year, month, day) {
    return `${day}. ${MONTH_NAMES_LOWERCASE[month]} ${year}`;
}

/**
 * Setup filter dropdown toggle behavior
 * @param {HTMLElement} container - The widget or list container
 * @param {string} cssPrefix - CSS class prefix (calendar-widget or calendar-list)
 */
export function setupFilterDropdowns(container, cssPrefix = 'calendar-widget') {
    const filterButtons = container.querySelectorAll(`.${cssPrefix}__filter-button`);
    
    // Toggle filter dropdowns (exclude date picker - it has its own handler)
    filterButtons.forEach(button => {
        // Skip date picker button
        if (button.getAttribute('data-filter') === 'date') {
            return;
        }
        
        button.addEventListener('click', function(e) {
            e.stopPropagation();
            const dropdown = this.parentElement;
            const isOpen = dropdown.classList.contains('is-open');
            
            // Close all dropdowns (including date picker)
            container.querySelectorAll(`.${cssPrefix}__filter-dropdown, .${cssPrefix}__date-picker`).forEach(d => {
                d.classList.remove('is-open');
            });
            
            // Toggle current dropdown
            if (!isOpen) {
                dropdown.classList.add('is-open');
            }
        });
    });
    
    // Close dropdowns when clicking outside (including date picker)
    document.addEventListener('click', function() {
        container.querySelectorAll(`.${cssPrefix}__filter-dropdown, .${cssPrefix}__date-picker`).forEach(d => {
            d.classList.remove('is-open');
        });
    });
    
    // Prevent dropdown content clicks from closing the dropdown
    container.querySelectorAll(`.${cssPrefix}__filter-dropdown__content`).forEach(content => {
        content.addEventListener('click', function(e) {
            e.stopPropagation();
        });
    });
}

/**
 * Setup custom date picker with month navigation and calendar grid
 * @param {HTMLElement} container - The widget or list container
 * @param {string} cssPrefix - CSS class prefix (calendar-widget or calendar-list)
 * @param {Function} onChangeCallback - Callback function when date changes
 */
export function setupCustomDatePicker(container, cssPrefix, onChangeCallback) {
    const datePicker = container.querySelector(`.${cssPrefix}__date-picker`);
    
    if (!datePicker) {
        return;
    }
    
    const button = datePicker.children[0];
    const content = datePicker.children[1];
    const hiddenInput = datePicker.children[2];
    
    if (!button || !content || !hiddenInput) {
        return;
    }
    
    const buttonLabel = button.querySelector('span');
    const monthLabel = content.querySelector(`.${cssPrefix}__date-picker__month-label`);
    const prevButton = content.querySelector(`.${cssPrefix}__date-picker__prev-month`);
    const nextButton = content.querySelector(`.${cssPrefix}__date-picker__next-month`);
    const daysContainer = content.querySelector(`.${cssPrefix}__date-picker__days`);
    
    // Current displayed month/year
    let currentDate = new Date();
    let displayedMonth = currentDate.getMonth();
    let displayedYear = currentDate.getFullYear();
    
    // Track selected date
    let selectedDay = null;
    let selectedMonth = null;
    let selectedYear = null;
    
    // Toggle dropdown
    button.addEventListener('click', function(e) {
        e.stopPropagation();
        const isOpen = datePicker.classList.contains('is-open');
        
        // Close all dropdowns
        container.querySelectorAll(`.${cssPrefix}__filter-dropdown, .${cssPrefix}__date-picker`).forEach(d => {
            d.classList.remove('is-open');
        });
        
        // Toggle current dropdown
        if (!isOpen) {
            datePicker.classList.add('is-open');
        }
    });
    
    // Close when clicking outside
    document.addEventListener('click', function() {
        datePicker.classList.remove('is-open');
    });
    
    // Prevent closing when clicking inside dropdown content
    content.addEventListener('click', function(e) {
        e.stopPropagation();
    });
    
    // Month navigation
    prevButton.addEventListener('click', function() {
        displayedMonth--;
        if (displayedMonth < 0) {
            displayedMonth = 11;
            displayedYear--;
        }
        renderCalendar();
    });
    
    nextButton.addEventListener('click', function() {
        displayedMonth++;
        if (displayedMonth > 11) {
            displayedMonth = 0;
            displayedYear++;
        }
        renderCalendar();
    });
    
    // Render calendar grid
    function renderCalendar() {
        monthLabel.textContent = `${MONTH_NAMES_CAPITALIZED[displayedMonth]} ${displayedYear}`;
        
        // Calculate days to display
        const firstDay = new Date(displayedYear, displayedMonth, 1);
        const lastDay = new Date(displayedYear, displayedMonth + 1, 0);
        
        // Get day of week (0 = Sunday, 1 = Monday, etc.)
        // Adjust so Monday = 0, Sunday = 6
        let startDayOfWeek = firstDay.getDay() - 1;
        if (startDayOfWeek < 0) startDayOfWeek = 6;
        
        // Build calendar grid
        daysContainer.innerHTML = '';
        
        // Add padding days from previous month
        if (startDayOfWeek > 0) {
            const prevMonthLastDay = new Date(displayedYear, displayedMonth, 0).getDate();
            for (let i = startDayOfWeek - 1; i >= 0; i--) {
                const day = prevMonthLastDay - i;
                const dayCell = createDayCell(day, 'prev-month');
                daysContainer.appendChild(dayCell);
            }
        }
        
        // Add days of current month
        for (let day = 1; day <= lastDay.getDate(); day++) {
            const isToday = day === currentDate.getDate() && 
                          displayedMonth === currentDate.getMonth() && 
                          displayedYear === currentDate.getFullYear();
            const dayCell = createDayCell(day, isToday ? 'today' : 'current-month', day);
            daysContainer.appendChild(dayCell);
        }
        
        // Add padding days from next month
        const totalCells = startDayOfWeek + lastDay.getDate();
        const remainingCells = 7 - (totalCells % 7);
        if (remainingCells < 7) {
            for (let day = 1; day <= remainingCells; day++) {
                const dayCell = createDayCell(day, 'next-month');
                daysContainer.appendChild(dayCell);
            }
        }
    }
    
    // Create a day cell
    function createDayCell(day, type, clickableDay = null) {
        const cell = document.createElement('button');
        cell.type = 'button';
        cell.className = `${cssPrefix}__date-picker__day`;
        cell.textContent = day;
        
        if (type === 'prev-month' || type === 'next-month') {
            cell.classList.add(`${cssPrefix}__date-picker__day--other-month`);
            cell.disabled = true;
        } else if (type === 'today') {
            cell.classList.add(`${cssPrefix}__date-picker__day--today`);
        }
        
        // Disable past dates (but not today)
        if (type === 'current-month' && clickableDay !== null) {
            const cellDate = new Date(displayedYear, displayedMonth, clickableDay);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            cellDate.setHours(0, 0, 0, 0);
            
            if (cellDate < today) {
                cell.disabled = true;
            }
        }
        
        // Check if this day is the selected date
        if (clickableDay !== null && 
            selectedDay === clickableDay && 
            selectedMonth === displayedMonth && 
            selectedYear === displayedYear) {
            cell.classList.add(`${cssPrefix}__date-picker__day--selected`);
        }
        
        // Add click handler for current month days (only if not disabled)
        if (clickableDay !== null && !cell.disabled) {
            cell.addEventListener('click', function() {
                selectDate(displayedYear, displayedMonth, clickableDay);
            });
        }
        
        return cell;
    }
    
    // Select a date
    function selectDate(year, month, day) {
        selectedDay = day;
        selectedMonth = month;
        selectedYear = year;
        
        hiddenInput.value = formatDateISO(year, month, day);
        buttonLabel.textContent = formatDateDanish(year, month, day);
        
        renderCalendar();
        datePicker.classList.remove('is-open');
        
        if (onChangeCallback) onChangeCallback();
    }
    
    // Initialize from existing value (URL params or previous selection)
    if (hiddenInput.value) {
        const date = new Date(hiddenInput.value);
        selectedDay = date.getDate();
        selectedMonth = date.getMonth();
        selectedYear = date.getFullYear();
        displayedMonth = selectedMonth;
        displayedYear = selectedYear;
        
        buttonLabel.textContent = formatDateDanish(selectedYear, selectedMonth, selectedDay);
    }
    
    // Initial render
    renderCalendar();
    
    // Expose method to set date programmatically (for URL population)
    datePicker._setDate = function(isoDate) {
        const date = new Date(isoDate);
        
        selectedDay = date.getDate();
        selectedMonth = date.getMonth();
        selectedYear = date.getFullYear();
        displayedMonth = selectedMonth;
        displayedYear = selectedYear;
        
        hiddenInput.value = isoDate;
        buttonLabel.textContent = formatDateDanish(selectedYear, selectedMonth, selectedDay);
        
        renderCalendar();
    };
}

/**
 * Get active filter values from container
 * @param {HTMLElement} container - The widget or list container
 * @returns {Object} Object with targetgroups, organisers, bydel, and fromDate arrays/values
 */
export function getActiveFilters(container) {
    const targetgroups = Array.from(container.querySelectorAll('input[name="targetgroups[]"]:checked'))
        .map(cb => cb.value);
    const organisers = Array.from(container.querySelectorAll('input[name="organisers[]"]:checked'))
        .map(cb => cb.value);
    const bydel = Array.from(container.querySelectorAll('input[name="bydel[]"]:checked'))
        .map(cb => cb.value);
    const dateInput = container.querySelector('input[name="fromDate"]');
    const fromDate = dateInput ? dateInput.value : null;
    
    return { targetgroups, organisers, bydel, fromDate };
}

/**
 * Render filter chips in the chips container
 * @param {Object} activeFilters - Active filter values
 * @param {HTMLElement} container - The widget or list container
 * @param {string} cssPrefix - CSS class prefix
 * @param {Function} onChipRemove - Callback when a chip is removed
 */
export function renderChips(activeFilters, container, cssPrefix, onChipRemove) {
    const chipsContainerWrapper = container.querySelector(`.${cssPrefix}__chips-container`);
    const chipsContainer = container.querySelector(`.${cssPrefix}__chips`);
    
    if (!chipsContainer || !chipsContainerWrapper) return;
    
    const hasFilters = activeFilters.targetgroups.length > 0 || 
                      activeFilters.organisers.length > 0 ||
                      activeFilters.bydel.length > 0 ||
                      activeFilters.fromDate;
    
    if (hasFilters) {
        chipsContainerWrapper.style.display = 'flex';
        chipsContainer.innerHTML = '';
        
        // Category chips
        activeFilters.targetgroups.forEach(value => {
            const checkbox = container.querySelector(`input[name="targetgroups[]"][value="${value}"]`);
            if (checkbox) {
                const label = checkbox.parentElement.textContent.trim();
                const chip = createChip(label, cssPrefix, () => {
                    checkbox.checked = false;
                    if (onChipRemove) onChipRemove();
                });
                chipsContainer.appendChild(chip);
            }
        });
        
        // Organiser chips
        activeFilters.organisers.forEach(value => {
            const checkbox = container.querySelector(`input[name="organisers[]"][value="${value}"]`);
            if (checkbox) {
                const label = checkbox.parentElement.textContent.trim();
                const chip = createChip(label, cssPrefix, () => {
                    checkbox.checked = false;
                    if (onChipRemove) onChipRemove();
                });
                chipsContainer.appendChild(chip);
            }
        });

        // Area chips
        activeFilters.bydel.forEach(value => {
            const checkbox = findCheckboxByValue(container, 'bydel[]', value);
            if (checkbox) {
                const label = checkbox.parentElement.textContent.trim();
                const chip = createChip(label, cssPrefix, () => {
                    checkbox.checked = false;
                    if (onChipRemove) onChipRemove();
                });
                chipsContainer.appendChild(chip);
            }
        });
        
        // Date chip
        if (activeFilters.fromDate) {
            const dateInput = container.querySelector('input[name="fromDate"]');
            const datePicker = container.querySelector('[class*="__date-picker"]');
            const dateObj = new Date(activeFilters.fromDate);
            const dateStr = formatDateDanish(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
            
            const chip = createChip(`Fra ${dateStr}`, cssPrefix, () => {
                if (dateInput) {
                    dateInput.value = '';
                    if (datePicker) {
                        const button = datePicker.querySelector(`.${cssPrefix}__filter-button span`);
                        if (button) button.textContent = getFilterLabelDatePlaceholder(container);
                    }
                }
                if (onChipRemove) onChipRemove();
            });
            chipsContainer.appendChild(chip);
        }
    } else {
        chipsContainerWrapper.style.display = 'none';
    }
}

/**
 * Create a chip element
 * @param {string} text - Chip label text
 * @param {string} cssPrefix - CSS class prefix
 * @param {Function} onRemove - Callback when chip is removed
 * @returns {HTMLElement} The chip element
 */
function createChip(text, cssPrefix, onRemove) {
    const chip = document.createElement('div');
    chip.className = `${cssPrefix}__chip`;
    chip.innerHTML = `
        <span>${text}</span>
        <button type="button" class="${cssPrefix}__chip__remove">×</button>
    `;
    chip.querySelector(`.${cssPrefix}__chip__remove`).addEventListener('click', onRemove);
    return chip;
}

/**
 * Update result counter display
 * @param {number} count - Number of results
 * @param {HTMLElement} container - The widget or list container
 * @param {string} cssPrefix - CSS class prefix
 */
export function updateCounter(count, container, cssPrefix) {
    const countElement = container.querySelector(`.${cssPrefix}__count`);
    if (countElement) {
        const countNumber = countElement.querySelector(`.${cssPrefix}__count__number`);
        if (countNumber) {
            countNumber.textContent = count;
        }
    }
}

/**
 * Build URL search params from filters
 * @param {Object} filters - Filter values
 * @param {Object} options - Additional options (nodeId, from, etc.)
 * @returns {URLSearchParams} URL search params object
 */
export function buildFilterParams(filters, options = {}) {
    const params = new URLSearchParams();
    
    // Add nodeId if provided
    if (options.nodeId) {
        params.append('nodeId', options.nodeId);
    }
    
    // Add from offset if provided
    if (options.from !== undefined) {
        params.append('from', options.from);
    }
    
    // Add filter parameters
    filters.targetgroups.forEach(val => params.append('targetgroups[]', val));
    filters.organisers.forEach(val => params.append('organisers[]', val));
    filters.bydel.forEach(val => params.append('bydel[]', val));
    if (filters.fromDate) {
        const timestamp = new Date(filters.fromDate).getTime();
        params.append('fromDate', timestamp);
    }
    
    return params;
}

/**
 * Create HTML for an event card
 * @param {Object} item - Event item from API
 * @param {string} eventDetailBaseUrl - Base URL for event detail page
 * @returns {string} HTML string for the event card
 */
export function createEventCardHTML(item, eventDetailBaseUrl = '') {
    const source = item._source;
    
    // Parse and format date
    const startDate = new Date(source.startdate.replace(' ', 'T'));
    const endDate = new Date(source.enddate.replace(' ', 'T'));
    const day = startDate.getDate();
    const month = MONTH_NAMES_ABBREVIATED[startDate.getMonth()].toUpperCase();
    
    // Format time
    const startTime = startDate.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    const endTime = endDate.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
    const timeRange = source.noenddate ? `Kl. ${startTime}` : `Kl. ${startTime}-${endTime}`;
    
    // Location
    const location = source.online ? 'Online' : source.locationname;
    
    // Generate clean detail URL with /event/ segment
    const detailUrl = eventDetailBaseUrl ? `${eventDetailBaseUrl}/event/${item._id}` : '#';
    
    return `
        <a href="${detailUrl}" class="calendar-event-card">
            <div class="calendar-event-card__date">
                <div class="calendar-event-card__date__day">${day}</div>
                <div class="calendar-event-card__date__month">${month}</div>
            </div>
            <div class="calendar-event-card__meta">
                <div class="calendar-event-card__time">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48613 2 2 6.48613 2 12C2 17.5139 6.48613 22 12 22C17.5139 22 22 17.5139 22 12C22 6.48613 17.5139 2 12 2ZM12 20.0009C7.5885 20.0009 3.99907 16.4117 3.99907 12C3.99907 7.58828 7.5885 3.99907 12 3.99907C16.4115 3.99907 20.0009 7.5885 20.0009 12C20.0009 16.4115 16.4115 20.0009 12 20.0009Z" fill="currentColor"/>
                        <path d="M12.9836 11.7113C12.9916 11.6602 12.9994 11.6092 12.9994 11.5559V7.19655C12.9994 6.64459 12.5518 6.19702 11.9998 6.19702C11.4479 6.19702 11.0003 6.64459 11.0003 7.19655V11.5559C11.0003 11.6409 10.9896 12.6085 11.3035 12.8899L14.3965 15.6626C14.5873 15.8339 14.8258 15.9178 15.0633 15.9178C15.3374 15.9178 15.6106 15.8056 15.808 15.5855C16.1765 15.1744 16.1421 14.5427 15.731 14.1742L12.9836 11.7113Z" fill="currentColor"/>
                    </svg>
                    ${timeRange}
                </div>
                <div class="calendar-event-card__location">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    ${location}
                </div>
            </div>
            <h3 class="calendar-event-card__title">${source.title}</h3>
            <div class="calendar-event-card__category">${source.category}</div>
        </a>
    `;
}

/**
 * Read URL query parameters and pre-populate filters
 * @param {HTMLElement} container - The widget or list container
 * @param {string} cssPrefix - CSS class prefix
 */
export function populateFiltersFromURL(container, cssPrefix) {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Pre-check targetgroups
    const targetgroups = urlParams.getAll('targetgroups[]');
    targetgroups.forEach(value => {
        const checkbox = container.querySelector(`input[name="targetgroups[]"][value="${value}"]`);
        if (checkbox) checkbox.checked = true;
    });
    
    // Pre-check organisers
    const organisers = urlParams.getAll('organisers[]');
    organisers.forEach(value => {
        const checkbox = container.querySelector(`input[name="organisers[]"][value="${value}"]`);
        if (checkbox) checkbox.checked = true;
    });

    // Pre-check bydel
    const bydel = urlParams.getAll('bydel[]');
    bydel.forEach(value => {
        const checkbox = findCheckboxByValue(container, 'bydel[]', value);
        if (checkbox) checkbox.checked = true;
    });
    
    // Pre-populate date
    const fromDate = urlParams.get('fromDate');
    if (fromDate) {
        const datePicker = container.querySelector(`.${cssPrefix}__date-picker`);
        if (datePicker && datePicker._setDate) {
            datePicker._setDate(fromDate);
        }
    }
}

function findCheckboxByValue(container, inputName, value) {
    const checkboxes = container.querySelectorAll(`input[name="${inputName}"]`);
    for (const checkbox of checkboxes) {
        if (checkbox.value === value) {
            return checkbox;
        }
    }
    return null;
}
